import { useEffect, useState } from 'react';
import {
  Alert, Badge, Button, Card, ColorInput, Code, CopyButton, Grid, Group, JsonInput,
  NumberInput, Select, Stack, Switch, Table, Tabs, Text, TextInput,
} from '@mantine/core';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import {
  IconAlertTriangle, IconCheck, IconCopy, IconKey, IconPalette, IconPlug,
  IconSettings, IconTrash, IconUsers,
} from '@tabler/icons-react';

import {
  useApiKeys, useCreateApiKey, useDeleteIntegration, useIntegrations, useInviteMember,
  useProject, useRemoveMember, useRevokeApiKey, useSaveBranding, useSaveIntegration,
  useSaveSettings, useTeam, useVerifyIntegration,
} from '../api/hooks';
import { DataState } from '../components/DataState';
import { PageHeader } from '../components/PageHeader';
import { CHANNEL_LABEL, fmtRelative } from '../theme/format';
import type { Branding, FrequencyCap, QuietHours } from '../api/types';

export function SettingsPage() {
  return (
    <>
      <PageHeader title="Settings" description="Everything scoped to this project." />
      <Tabs defaultValue="integrations">
        <Tabs.List mb="lg">
          <Tabs.Tab value="integrations" leftSection={<IconPlug size={14} />}>
            Integrations
          </Tabs.Tab>
          <Tabs.Tab value="branding" leftSection={<IconPalette size={14} />}>
            Branding
          </Tabs.Tab>
          <Tabs.Tab value="delivery" leftSection={<IconSettings size={14} />}>
            Delivery rules
          </Tabs.Tab>
          <Tabs.Tab value="keys" leftSection={<IconKey size={14} />}>
            API keys
          </Tabs.Tab>
          <Tabs.Tab value="team" leftSection={<IconUsers size={14} />}>
            Team
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="integrations"><IntegrationsTab /></Tabs.Panel>
        <Tabs.Panel value="branding"><BrandingTab /></Tabs.Panel>
        <Tabs.Panel value="delivery"><DeliveryTab /></Tabs.Panel>
        <Tabs.Panel value="keys"><KeysTab /></Tabs.Panel>
        <Tabs.Panel value="team"><TeamTab /></Tabs.Panel>
      </Tabs>
    </>
  );
}

// ── Integrations ──────────────────────────────────────────────────

const PROVIDERS: Record<string, Array<{ value: string; label: string; creds: string; config: string }>> = {
  push: [
    {
      value: 'fcm', label: 'Firebase Cloud Messaging',
      creds: '{\n  "type": "service_account",\n  "project_id": "…",\n  "private_key": "…",\n  "client_email": "…"\n}',
      config: '{}',
    },
    {
      value: 'apns', label: 'Apple Push Notification service',
      creds: '{\n  "auth_key_p8": "-----BEGIN PRIVATE KEY-----\\n…",\n  "key_id": "…",\n  "team_id": "…"\n}',
      config: '{\n  "bundle_id": "com.acme.app",\n  "environment": "production"\n}',
    },
    {
      value: 'webpush', label: 'Web Push (VAPID)',
      creds: '{\n  "vapid_public_key": "…",\n  "vapid_private_key": "…",\n  "subject": "mailto:ops@acme.com"\n}',
      config: '{}',
    },
  ],
  email: [
    {
      value: 'resend', label: 'Resend',
      creds: '{\n  "api_key": "re_…",\n  "webhook_secret": "whsec_…"\n}',
      config: '{\n  "from_email": "hello@acme.com"\n}',
    },
    {
      value: 'ses', label: 'Amazon SES',
      creds: '{\n  "access_key_id": "…",\n  "secret_access_key": "…",\n  "region": "ap-south-1"\n}',
      config: '{\n  "from_email": "hello@acme.com",\n  "configuration_set": "seidhi-events"\n}',
    },
  ],
  sms: [
    {
      value: 'twilio', label: 'Twilio',
      creds: '{\n  "account_sid": "AC…",\n  "auth_token": "…"\n}',
      config: '{\n  "from_number": "+15551234567",\n  "default_country_code": "1"\n}',
    },
    {
      value: 'msg91', label: 'MSG91 (India)',
      creds: '{\n  "auth_key": "…"\n}',
      config: '{\n  "sender_id": "ACMEIN",\n  "dlt_entity_id": "…",\n  "default_country_code": "91"\n}',
    },
  ],
  whatsapp: [
    {
      value: 'gupshup', label: 'Gupshup',
      creds: '{\n  "api_key": "…",\n  "app_name": "…",\n  "webhook_token": "…"\n}',
      config: '{\n  "source_number": "919876543210",\n  "default_country_code": "91"\n}',
    },
  ],
};

function IntegrationsTab() {
  const { data, isLoading, error } = useIntegrations();
  const verify = useVerifyIntegration();
  const remove = useDeleteIntegration();

  return (
    <Stack>
      <Alert color="blue" variant="light">
        <Text size="sm">
          Credentials are envelope-encrypted before they reach the database and are
          never returned by any endpoint — not even to this page. Bring your own
          provider accounts: it keeps deliverability reputation and DLT liability
          with the party who actually controls them.
        </Text>
      </Alert>

      <Group>
        {(['push', 'email', 'sms', 'whatsapp'] as const).map((channel) => (
          <Button
            key={channel} size="xs" variant="light"
            onClick={() =>
              modals.open({
                title: `Configure ${CHANNEL_LABEL[channel]}`,
                size: 'lg',
                children: <IntegrationForm channel={channel} />,
              })
            }
          >
            Add {CHANNEL_LABEL[channel]}
          </Button>
        ))}
      </Group>

      <DataState
        loading={isLoading}
        error={error}
        empty={!data?.length}
        emptyTitle="No integrations yet"
        emptyDescription="Nothing can be sent until at least one channel is configured."
      >
        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Channel</Table.Th>
              <Table.Th>Provider</Table.Th>
              <Table.Th>Status</Table.Th>
              <Table.Th>Verified</Table.Th>
              <Table.Th />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {data?.map((i) => (
              <Table.Tr key={i.id}>
                <Table.Td>
                  <Badge variant="light">{CHANNEL_LABEL[i.channel] ?? i.channel}</Badge>
                </Table.Td>
                <Table.Td><Text size="sm" fw={500}>{i.provider}</Text></Table.Td>
                <Table.Td>
                  <Badge color={i.status === 'active' ? 'teal' : 'red'}>
                    {i.status}
                  </Badge>
                  {i.status === 'halted' && (
                    <Text size="xs" c="red" mt={2}>
                      Halted after a credential failure — verify to re-enable.
                    </Text>
                  )}
                </Table.Td>
                <Table.Td>
                  <Text size="xs" c="dimmed">
                    {i.verified_at ? fmtRelative(i.verified_at) : 'never'}
                  </Text>
                </Table.Td>
                <Table.Td ta="right">
                  <Group gap={4} justify="flex-end">
                    <Button
                      size="compact-xs" variant="light"
                      loading={verify.isPending}
                      onClick={() =>
                        verify.mutate(i.id, {
                          onSuccess: (res) =>
                            notifications.show({
                              color: res.verified ? 'teal' : 'red',
                              title: res.verified ? 'Connection works' : 'Verification failed',
                              message: res.verified
                                ? `${i.provider} accepted these credentials.`
                                : res.error?.message ?? 'The provider rejected them.',
                              autoClose: res.verified ? 4000 : 10000,
                            }),
                        })
                      }
                    >
                      Test connection
                    </Button>
                    <Button
                      size="compact-xs" variant="subtle" color="red"
                      onClick={() =>
                        modals.openConfirmModal({
                          title: `Remove ${i.provider}?`,
                          children: (
                            <Text size="sm">
                              Sends on this channel will fail until another provider is
                              configured.
                            </Text>
                          ),
                          labels: { confirm: 'Remove', cancel: 'Keep' },
                          confirmProps: { color: 'red' },
                          onConfirm: () => remove.mutate(i.id),
                        })
                      }
                    >
                      Remove
                    </Button>
                  </Group>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </DataState>
    </Stack>
  );
}

function IntegrationForm({ channel }: { channel: string }) {
  const options = PROVIDERS[channel] ?? [];
  const [provider, setProvider] = useState(options[0]?.value ?? '');
  const chosen = options.find((o) => o.value === provider);
  const [creds, setCreds] = useState(chosen?.creds ?? '{}');
  const [config, setConfig] = useState(chosen?.config ?? '{}');
  const save = useSaveIntegration();

  useEffect(() => {
    setCreds(chosen?.creds ?? '{}');
    setConfig(chosen?.config ?? '{}');
  }, [chosen]);

  return (
    <Stack>
      <Select
        label="Provider"
        data={options}
        value={provider}
        onChange={(v) => setProvider(v ?? '')}
      />
      <JsonInput
        label="Credentials"
        description="Encrypted before storage. Never returned by the API."
        autosize minRows={6} formatOnBlur validationError="Not valid JSON"
        value={creds}
        onChange={setCreds}
      />
      <JsonInput
        label="Configuration"
        description="Non-secret settings: sender addresses, bundle ids, DLT entity id"
        autosize minRows={3} formatOnBlur validationError="Not valid JSON"
        value={config}
        onChange={setConfig}
      />
      <Button
        loading={save.isPending}
        onClick={() => {
          try {
            save.mutate(
              {
                channel, provider,
                credentials: JSON.parse(creds),
                config: JSON.parse(config),
              },
              {
                onSuccess: () => {
                  notifications.show({
                    message: 'Saved. Use "Test connection" to confirm it works.',
                    color: 'teal',
                  });
                  modals.closeAll();
                },
                onError: (e) =>
                  notifications.show({ color: 'red', message: (e as Error).message }),
              },
            );
          } catch {
            notifications.show({ color: 'red', message: 'Credentials or config are not valid JSON' });
          }
        }}
      >
        Save integration
      </Button>
    </Stack>
  );
}

// ── Branding ──────────────────────────────────────────────────────

function BrandingTab() {
  const { data: project } = useProject();
  const save = useSaveBranding();
  const [branding, setBranding] = useState<Branding>({});

  useEffect(() => {
    if (project) setBranding(project.branding ?? {});
  }, [project]);

  const set = (patch: Partial<Branding>) => setBranding({ ...branding, ...patch });

  return (
    <Grid gutter="lg">
      <Grid.Col span={{ base: 12, md: 7 }}>
        <Card>
          <Alert color="blue" variant="light" mb="md">
            <Text size="sm">
              Branding reaches exactly three end-user-visible surfaces: email chrome,
              the hosted preference center, and push notification defaults. It never
              touches this dashboard — that is yours, and your clients do not log in here.
            </Text>
          </Alert>

          <Stack>
            <Group grow>
              <TextInput
                label="Display name"
                placeholder="Acme Rewards"
                description="Shown as the sender in email footers and the preference center"
                value={branding.display_name ?? ''}
                onChange={(e) => set({ display_name: e.currentTarget.value })}
              />
              <ColorInput
                label="Primary colour"
                value={branding.primary_color ?? '#0F62FE'}
                onChange={(v) => set({ primary_color: v })}
              />
            </Group>

            <TextInput
              label="Logo URL"
              placeholder="https://acme.com/logo.png"
              value={branding.logo_url ?? ''}
              onChange={(e) => set({ logo_url: e.currentTarget.value })}
            />

            <Group grow>
              <TextInput
                label="From name"
                value={branding.email_from_name ?? ''}
                onChange={(e) => set({ email_from_name: e.currentTarget.value })}
              />
              <TextInput
                label="From address"
                description="Required for email. Sending fails loudly without it rather than falling back to the operator's domain."
                value={branding.email_from_address ?? ''}
                onChange={(e) => set({ email_from_address: e.currentTarget.value })}
              />
            </Group>

            <TextInput
              label="Reply-to"
              value={branding.reply_to ?? ''}
              onChange={(e) => set({ reply_to: e.currentTarget.value })}
            />

            <TextInput
              label="Support email"
              description="Shown on the preference center"
              value={branding.preference_center?.support_email ?? ''}
              onChange={(e) =>
                set({
                  preference_center: {
                    ...branding.preference_center,
                    support_email: e.currentTarget.value,
                  },
                })
              }
            />

            <Button
              loading={save.isPending}
              disabled={!project}
              onClick={() =>
                save.mutate(
                  { id: project!.id, ...branding },
                  { onSuccess: () => notifications.show({ message: 'Branding saved', color: 'teal' }) },
                )
              }
            >
              Save branding
            </Button>
          </Stack>
        </Card>
      </Grid.Col>

      <Grid.Col span={{ base: 12, md: 5 }}>
        <Card>
          <Text fw={500} mb="sm">How it appears</Text>
          <Stack
            gap={0}
            style={{
              border: '1px solid var(--mantine-color-default-border)',
              borderRadius: 8, overflow: 'hidden',
            }}
          >
            <div style={{ height: 4, background: branding.primary_color ?? '#0F62FE' }} />
            <Stack p="md" gap="xs">
              {branding.logo_url && (
                <img
                  src={branding.logo_url}
                  alt=""
                  style={{ maxHeight: 32, objectFit: 'contain', alignSelf: 'flex-start' }}
                />
              )}
              <Text size="sm">Your message content appears here.</Text>
              <Text size="xs" c="dimmed" mt="md" pt="xs"
                style={{ borderTop: '1px solid var(--mantine-color-default-border)' }}>
                Sent by {branding.display_name || 'your project'}.<br />
                Manage preferences · Unsubscribe
                {branding.preference_center?.support_email && (
                  <><br />Questions? {branding.preference_center.support_email}</>
                )}
              </Text>
            </Stack>
          </Stack>
          <Text size="xs" c="dimmed" mt="sm">
            The unsubscribe link is added automatically to marketing email. Transactional
            mail gets a preferences link instead — nobody should be able to unsubscribe
            from their own receipts.
          </Text>
        </Card>
      </Grid.Col>
    </Grid>
  );
}

// ── Delivery rules ────────────────────────────────────────────────

function DeliveryTab() {
  const { data: project } = useProject();
  const save = useSaveSettings();

  const [timezone, setTimezone] = useState('UTC');
  const [retentionDays, setRetentionDays] = useState(395);
  const [quietEnabled, setQuietEnabled] = useState(false);
  const [quiet, setQuiet] = useState<QuietHours>({
    start: '21:00', end: '08:00', respect_profile_tz: true,
  });
  const [capEnabled, setCapEnabled] = useState(false);
  const [cap, setCap] = useState<FrequencyCap>({ max_per_day: 3, max_per_week: 10 });

  useEffect(() => {
    if (!project) return;
    setTimezone(project.timezone);
    setRetentionDays(project.retention_days);
    if (project.quiet_hours) {
      setQuietEnabled(true);
      setQuiet(project.quiet_hours);
    }
    if (project.frequency_cap) {
      setCapEnabled(true);
      setCap(project.frequency_cap);
    }
  }, [project]);

  return (
    <Grid gutter="lg">
      <Grid.Col span={{ base: 12, md: 6 }}>
        <Stack>
          <Card>
            <Text fw={500} mb="md">Quiet hours</Text>
            <Stack>
              <Switch
                label="Hold non-urgent messages overnight"
                description="Deferred to the end of the window rather than dropped, so a recipient gets it late instead of never."
                checked={quietEnabled}
                onChange={(e) => setQuietEnabled(e.currentTarget.checked)}
              />
              {quietEnabled && (
                <>
                  <Group grow>
                    <TextInput
                      label="From" placeholder="21:00"
                      value={quiet.start}
                      onChange={(e) => setQuiet({ ...quiet, start: e.currentTarget.value })}
                    />
                    <TextInput
                      label="Until" placeholder="08:00"
                      value={quiet.end}
                      onChange={(e) => setQuiet({ ...quiet, end: e.currentTarget.value })}
                    />
                  </Group>
                  <Switch
                    label="Use each recipient's own timezone"
                    description="A project spanning three timezones has three different 9pm."
                    checked={quiet.respect_profile_tz}
                    onChange={(e) =>
                      setQuiet({ ...quiet, respect_profile_tz: e.currentTarget.checked })
                    }
                  />
                  <Alert color="gray" variant="light">
                    <Text size="xs">
                      Transactional, billing, and security messages ignore quiet hours.
                      A password reset held until 8am is a broken product.
                    </Text>
                  </Alert>
                </>
              )}
            </Stack>
          </Card>

          <Card>
            <Text fw={500} mb="md">Frequency cap</Text>
            <Stack>
              <Switch
                label="Limit how much one person receives"
                checked={capEnabled}
                onChange={(e) => setCapEnabled(e.currentTarget.checked)}
              />
              {capEnabled && (
                <>
                  <Group grow>
                    <NumberInput
                      label="Per day" min={0}
                      value={cap.max_per_day ?? 0}
                      onChange={(v) => setCap({ ...cap, max_per_day: Number(v) || 0 })}
                    />
                    <NumberInput
                      label="Per week" min={0}
                      value={cap.max_per_week ?? 0}
                      onChange={(v) => setCap({ ...cap, max_per_week: Number(v) || 0 })}
                    />
                  </Group>
                  <Alert color="gray" variant="light">
                    <Text size="xs">
                      Transactional and security messages are exempt. A receipt withheld
                      because someone already got three marketing pushes is a bug with
                      legal consequences.
                    </Text>
                  </Alert>
                </>
              )}
            </Stack>
          </Card>
        </Stack>
      </Grid.Col>

      <Grid.Col span={{ base: 12, md: 6 }}>
        <Card>
          <Text fw={500} mb="md">Project</Text>
          <Stack>
            <TextInput
              label="Timezone"
              description="Used for quiet hours when the recipient's own is unknown, and for daily rollups"
              value={timezone}
              onChange={(e) => setTimezone(e.currentTarget.value)}
            />
            <NumberInput
              label="Retention (days)"
              description="Events and delivery rows past this window are hard-deleted nightly. Daily totals are rolled up first, so aggregate history survives."
              min={1} max={3650}
              value={retentionDays}
              onChange={(v) => setRetentionDays(Number(v) || 395)}
            />
            <Button
              loading={save.isPending}
              disabled={!project}
              onClick={() =>
                save.mutate(
                  {
                    id: project!.id,
                    timezone,
                    retention_days: retentionDays,
                    quiet_hours: quietEnabled ? quiet : null,
                    frequency_cap: capEnabled ? cap : null,
                  },
                  {
                    onSuccess: () =>
                      notifications.show({ message: 'Settings saved', color: 'teal' }),
                    onError: (e) =>
                      notifications.show({ color: 'red', message: (e as Error).message }),
                  },
                )
              }
            >
              Save settings
            </Button>
          </Stack>
        </Card>
      </Grid.Col>
    </Grid>
  );
}

// ── API keys ──────────────────────────────────────────────────────

function KeysTab() {
  const { data, isLoading, error } = useApiKeys();
  const create = useCreateApiKey();
  const revoke = useRevokeApiKey();

  return (
    <Stack>
      <Alert color="blue" variant="light">
        <Text size="sm">
          A <Code>pk_</Code> key is meant to ship inside your client apps: it can write
          profiles, events, and devices, and nothing else. A <Code>sk_</Code> key can
          send, so it belongs on your servers only.
        </Text>
      </Alert>

      <Group>
        <Button
          size="xs"
          onClick={() =>
            modals.open({
              title: 'New API key',
              children: (
                <NewKeyForm
                  onCreate={(input) =>
                    create.mutate(input, {
                      onSuccess: (key) => {
                        modals.closeAll();
                        modals.open({
                          title: 'Copy this key now',
                          children: <RevealKey value={key.key ?? ''} />,
                        });
                      },
                    })
                  }
                />
              ),
            })
          }
        >
          Create key
        </Button>
      </Group>

      <DataState loading={isLoading} error={error} empty={!data?.length}
        emptyTitle="No API keys" emptyDescription="Your SDKs need one to send anything.">
        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Name</Table.Th>
              <Table.Th>Prefix</Table.Th>
              <Table.Th>Scope</Table.Th>
              <Table.Th>Last used</Table.Th>
              <Table.Th />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {data?.map((k) => (
              <Table.Tr key={k.id} opacity={k.revoked_at ? 0.5 : 1}>
                <Table.Td><Text size="sm">{k.name}</Text></Table.Td>
                <Table.Td><Code>{k.prefix}…</Code></Table.Td>
                <Table.Td>
                  <Badge color={k.scope === 'secret' ? 'orange' : 'gray'}>{k.scope}</Badge>
                </Table.Td>
                <Table.Td>
                  <Text size="xs" c="dimmed">
                    {k.revoked_at ? `revoked ${fmtRelative(k.revoked_at)}` : fmtRelative(k.last_used_at)}
                  </Text>
                </Table.Td>
                <Table.Td ta="right">
                  {!k.revoked_at && (
                    <Button
                      size="compact-xs" variant="subtle" color="red"
                      leftSection={<IconTrash size={12} />}
                      onClick={() =>
                        modals.openConfirmModal({
                          title: 'Revoke this key?',
                          children: (
                            <Text size="sm">
                              Anything using it stops working immediately.
                            </Text>
                          ),
                          labels: { confirm: 'Revoke', cancel: 'Keep' },
                          confirmProps: { color: 'red' },
                          onConfirm: () => revoke.mutate(k.id),
                        })
                      }
                    >
                      Revoke
                    </Button>
                  )}
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </DataState>
    </Stack>
  );
}

function NewKeyForm({ onCreate }: { onCreate: (i: { name: string; scope: string }) => void }) {
  const [name, setName] = useState('');
  const [scope, setScope] = useState('secret');
  return (
    <Stack>
      <TextInput
        label="Name" placeholder="iOS app" data-autofocus
        value={name} onChange={(e) => setName(e.currentTarget.value)}
      />
      <Select
        label="Scope"
        data={[
          { value: 'public', label: 'Public — for client apps, cannot send' },
          { value: 'secret', label: 'Secret — server-side, can send' },
        ]}
        value={scope}
        onChange={(v) => setScope(v ?? 'secret')}
      />
      <Button disabled={!name} onClick={() => onCreate({ name, scope })}>Create</Button>
    </Stack>
  );
}

function RevealKey({ value }: { value: string }) {
  return (
    <Stack>
      <Alert color="yellow" variant="light" icon={<IconAlertTriangle size={16} />}>
        <Text size="sm">
          This is the only time this key is shown. Only a hash is stored, so it cannot
          be recovered — that is what makes a database dump not a set of live keys.
        </Text>
      </Alert>
      <Code block style={{ wordBreak: 'break-all' }}>{value}</Code>
      <CopyButton value={value}>
        {({ copied, copy }) => (
          <Button
            color={copied ? 'teal' : undefined}
            leftSection={copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
            onClick={copy}
          >
            {copied ? 'Copied' : 'Copy key'}
          </Button>
        )}
      </CopyButton>
    </Stack>
  );
}

// ── Team ──────────────────────────────────────────────────────────

function TeamTab() {
  const { data, isLoading, error } = useTeam();
  const invite = useInviteMember();
  const remove = useRemoveMember();

  return (
    <Stack>
      <Alert color="blue" variant="light">
        <Text size="sm">
          Owners can change integrations, API keys, branding, and membership. Members
          can send and read everything else.
        </Text>
      </Alert>

      <Group>
        <Button
          size="xs"
          onClick={() =>
            modals.open({
              title: 'Add a team member',
              children: (
                <InviteForm
                  onSubmit={(input) =>
                    invite.mutate(input, {
                      onSuccess: () => {
                        notifications.show({ message: 'Member added', color: 'teal' });
                        modals.closeAll();
                      },
                      onError: (e) =>
                        notifications.show({ color: 'red', message: (e as Error).message }),
                    })
                  }
                />
              ),
            })
          }
        >
          Add member
        </Button>
      </Group>

      <DataState loading={isLoading} error={error} empty={!data?.length}>
        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Email</Table.Th>
              <Table.Th>Name</Table.Th>
              <Table.Th>Role</Table.Th>
              <Table.Th>Last signed in</Table.Th>
              <Table.Th />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {data?.map((m) => (
              <Table.Tr key={m.id}>
                <Table.Td><Text size="sm">{m.email}</Text></Table.Td>
                <Table.Td><Text size="sm" c="dimmed">{m.name || '—'}</Text></Table.Td>
                <Table.Td>
                  <Badge color={m.role === 'owner' ? 'brand' : 'gray'}>{m.role}</Badge>
                </Table.Td>
                <Table.Td>
                  <Text size="xs" c="dimmed">{fmtRelative(m.last_login_at)}</Text>
                </Table.Td>
                <Table.Td ta="right">
                  <Button
                    size="compact-xs" variant="subtle" color="red"
                    onClick={() =>
                      modals.openConfirmModal({
                        title: `Remove ${m.email}?`,
                        children: (
                          <Text size="sm">
                            Their sessions are revoked immediately.
                          </Text>
                        ),
                        labels: { confirm: 'Remove', cancel: 'Keep' },
                        confirmProps: { color: 'red' },
                        onConfirm: () =>
                          remove.mutate(m.id, {
                            onError: (e) =>
                              notifications.show({ color: 'red', message: (e as Error).message }),
                          }),
                      })
                    }
                  >
                    Remove
                  </Button>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </DataState>
    </Stack>
  );
}

function InviteForm({
  onSubmit,
}: {
  onSubmit: (i: { email: string; password: string; name: string; role: string }) => void;
}) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState('member');

  return (
    <Stack>
      <Alert color="gray" variant="light">
        <Text size="xs">
          You set an initial password and share it out of band. Emailed invitation
          links need a transactional sender for the platform&apos;s own mail, which is
          a separate thing from a tenant&apos;s configured integration — this is the
          honest interim.
        </Text>
      </Alert>
      <TextInput label="Email" data-autofocus value={email}
        onChange={(e) => setEmail(e.currentTarget.value)} />
      <TextInput label="Name" value={name} onChange={(e) => setName(e.currentTarget.value)} />
      <TextInput
        label="Initial password"
        description="At least 12 characters"
        value={password}
        onChange={(e) => setPassword(e.currentTarget.value)}
      />
      <Select
        label="Role"
        data={[
          { value: 'member', label: 'Member — can send and read' },
          { value: 'owner', label: 'Owner — can also change credentials and membership' },
        ]}
        value={role}
        onChange={(v) => setRole(v ?? 'member')}
      />
      <Button
        disabled={!email || password.length < 12}
        onClick={() => onSubmit({ email, password, name, role })}
      >
        Add member
      </Button>
    </Stack>
  );
}
